import { useRemoteClientPost, useRemoteClientPut } from '@/configs/http-client'
import { Endpoints } from '@/remote/endpoints'
import { useLocalStorage } from '@/remote/local-storage'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { useHookFormMask } from 'use-mask-input'
import { Button } from '../components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form'
import { Input } from '../components/ui/input'
import { useToast } from '../hooks'
import { formatCPF } from '../utils/formatDocuments'
import { formatPhone, removeNonNumeric } from '../utils/formatPhone'
import { ClientFormData, clientSchema } from '../validators/client-validator'

interface ClientFormProps {
  data?: ClientFormData & { id?: number }
  closeDialog?: () => void
  updateList?: () => void
}

export default function ClientForm({ data, closeDialog, updateList }: ClientFormProps) {
  const { httpClientPost } = useRemoteClientPost()
  const { httpClientPut } = useRemoteClientPut()
  const { getUnitId } = useLocalStorage()
  const { showToast } = useToast()
  const [params] = useSearchParams()
  const isEditMode = Boolean(data?.id)

  const form = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      nome: data?.nome || '',
      cpf: formatCPF(data?.cpf) || '',
      email: data?.email || '',
      telefone: formatPhone(data?.telefone) || '',
      unidade: {
        id: data?.unidade?.id || params.get('unit') || getUnitId()
      }
    }
  })

  // const fetchUnits = async () => {
  //   try {
  //     const response = await remoteClientGet(Endpoints.units, formatParams({
  //       page: 0,
  //       size: 1000,
  //       sortParam: 'id',
  //       order: 'desc',
  //       idUnidade: getUnitId()
  //     }));
  //     const data = response?.data?.data?.content;
  //     //console.log(data)
  //     setDataUnits(data.map((unit: any) => ({
  //       ...unit,
  //       label: unit?.nomeFantasia,
  //       value: unit?.id
  //     })));
  //     //console.log(dataUnits)
  //   } catch (error) {
  //     console.error(error);
  //     throw error
  //   }
  // }

  // useEffect(() => {
  //   //console.log('fetching units')
  //   fetchUnits();
  // }, []);

  const onSubmit = async (formData: ClientFormData) => {
    const formattedData = { ...formData, id: data?.id, telefone: removeNonNumeric(formData?.telefone), cpf: removeNonNumeric(formData?.cpf) }

    try {
      if (isEditMode) {
        await httpClientPut(Endpoints.client, { ...formattedData })
        closeDialog?.()
        updateList?.()
      } else {
        delete formattedData.id
        await httpClientPost(Endpoints.client, { ...formattedData })
      }

      showToast('Requisição enviada com sucesso!', 'success')
      form.reset()
    } catch (error: any) {
      console.error('Erro no envio:', error?.response?.data?.errors)
      const errors = error?.response?.data?.errors
      if (errors) {
        for (const key in errors) {
          showToast(errors[key], 'error')
        }
      }
      showToast('Erro ao enviar requisição', 'error')
    }
  }

  const registerWithMask = useHookFormMask(form.register)

  const formValues = form.watch()

  useEffect(() => {
    console.log(formValues)
  }, [formValues])

  const {
    formState: { errors }
  } = form

  useEffect(() => {
    console.log('Erros do formulário:', errors)
  }, [errors])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="max-w-128 p-4 w-full flex flex-col m-auto">
        <div className="grid grid-cols-2 gap-3 w-full">
          <FormField
            control={form.control}
            name="nome"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span>
                  Nome
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Nome" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.nome?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="cpf"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span>
                  CPF
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    {...registerWithMask('cpf', 'cpf', {
                      required: true
                    })}
                    placeholder="000.000.000-00"
                  />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.cpf?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-3 w-full">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span>
                  Email
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Email" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.email?.message}</FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="telefone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <span className="text-gray-400">*</span>
                  Telefone
                </FormLabel>
                <FormControl>
                  <Input {...field} {...registerWithMask('telefone', '(99) 99999-9999')} placeholder="(00) 00000-0000" />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors.telefone?.message}</FormMessage>
              </FormItem>
            )}
          />

          {/* <FormField
            control={form.control}
            name="unidade.id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Unidade</FormLabel>
                <FormControl>
                  <ComboSelectWithLabel
                    defaultValue={field.value}
                    data={dataUnits}
                    onChange={(e) => {
                      //console.log(e)
                      const id = e?.value;
                      form.setValue('unidade.id', id);
                      form.trigger('unidade.id');
                    }}
                  />
                </FormControl>
                <FormMessage className="text-xs">
                  {form.formState.errors.unidade?.id?.message}
                </FormMessage>
              </FormItem>
            )}
          /> */}
        </div>

        <Button type="submit" className="bg-acelerafit-green-200 hover:bg-acelerafit-green-50 text-muted mt-7">
          Enviar
        </Button>
      </form>
    </Form>
  )
}
